import { useLayoutEffect, useState, type RefObject } from 'react'

// Whether a scrollable element has more content past its left or right edge —
// or, with `axis: 'y'`, past its top or bottom (reported as overflowLeft /
// overflowRight: start / end) — for fading the clipped side rather than
// cutting it straight off.
// Re-measures on scroll and on any resize of the container or its children —
// so the scrolled content must size to itself (`w-max`), not to the container,
// or a pill appended after mount grows nothing the observer can see.
export function useScrollEdges(ref: RefObject<HTMLElement | null>, axis: 'x' | 'y' = 'x'): {
  overflowLeft: boolean
  overflowRight: boolean
} {
  const [edges, setEdges] = useState({ overflowLeft: false, overflowRight: false })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const max = axis === 'x' ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight
      const at = axis === 'x' ? el.scrollLeft : el.scrollTop
      const next = { overflowLeft: at > 1, overflowRight: at < max - 1 }
      setEdges(prev =>
        prev.overflowLeft === next.overflowLeft && prev.overflowRight === next.overflowRight
          ? prev
          : next,
      )
    }
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    for (const child of el.children) ro.observe(child)
    return () => {
      el.removeEventListener('scroll', measure)
      ro.disconnect()
    }
  }, [ref, axis])

  return edges
}
